import * as React from "react"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { Input } from "@digits-shared/DesignSystem/FormElements/Input"
import { EMAIL_ADDRESS_PATTERN_REGEX } from "@digits-shared/helpers/emailAddressPattern"
import { useFormTracking } from "@digits-shared/hooks/useFormTracking"
import useRouter from "@digits-shared/hooks/useRouter"
import colors from "@digits-shared/themes/colors"
import fonts from "@digits-shared/themes/typography"
import styled from "styled-components"
import { ButtonWrapper } from "src/frontend/components/Public/Shared/RegistrationElements"
import { PASSWORD_RESET_RECIPIENT } from "src/frontend/components/Session/Glow/PasswordReset/locationState"
import { useCreatePasswordReset } from "src/frontend/components/Session/Glow/PasswordReset/useCreatePasswordReset"
import { Header } from "src/frontend/components/Shared/Glow/Header"
import routes from "src/frontend/routes"

enum FormField {
  Email = "emailAddress",
}

/*
  STYLES
*/

const RequestResetForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const PromptText = styled.div`
  font-size: 12px;

  font-weight: ${fonts.weight.normal};
  color: ${colors.secondary};
`

/*
  COMPONENTS
*/

export const CreatePasswordReset: React.FC = () => {
  const { location, history } = useRouter()
  const initialFormState = React.useMemo(
    () => ({
      initialValues: {
        [FormField.Email]: typeof location.state?.email === "string" ? location.state?.email : "",
      },
    }),
    [location.state?.email]
  )
  const { onFieldBlurred, onFieldChanged, isFieldInvalid, fieldHasValidValue, getFieldValue } =
    useFormTracking<FormField>(initialFormState)

  const onCompleted = React.useCallback(() => {
    const emailAddress = getFieldValue(FormField.Email) || ""
    const state = { [PASSWORD_RESET_RECIPIENT]: emailAddress }
    history.replace(routes.passwordReset.generate(), state)
  }, [history, getFieldValue])

  const { createPasswordReset, loading } = useCreatePasswordReset(onCompleted)

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      event.stopPropagation()

      const emailAddress = getFieldValue(FormField.Email) || ""
      createPasswordReset(emailAddress)
    },
    [createPasswordReset, getFieldValue]
  )

  const isEmailInvalid = isFieldInvalid(FormField.Email)
  const isEmailValid = fieldHasValidValue(FormField.Email)

  return (
    <>
      <Header to={routes.login.generate()}>Forgot Password</Header>
      <PromptText>
        Enter your email address and we'll send you a link to reset your password.
      </PromptText>
      <RequestResetForm onSubmit={onSubmit}>
        <Input
          id={FormField.Email}
          name={FormField.Email}
          placeholder="Enter email"
          label="Enter email"
          hideLabel
          type="email"
          autoComplete="email"
          autoFocus
          onChange={onFieldChanged}
          onBlur={onFieldBlurred}
          pattern={EMAIL_ADDRESS_PATTERN_REGEX}
          required
          invalid={isEmailInvalid}
          disabled={loading}
        />
        <ButtonWrapper disabled={!isEmailValid}>
          <DigitsButton
            $fullWidth
            id="request-reset-password"
            disabled={!isEmailValid || loading}
            type="submit"
            label="Reset Password"
          />
        </ButtonWrapper>
      </RequestResetForm>
    </>
  )
}
